import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { UsersThree, PencilSimple, Trash, Plus } from "@phosphor-icons/react";

export function ContactPicker({ selected, setSelected }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ total: 0, rows: [] });
  useEffect(() => {
    const t = setTimeout(() => api.get("/contact-groups/contacts", { params: { q, limit: 200 } }).then(({ data: d }) => setData(d)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selectAll = async () => {
    const { data: d } = await api.get("/contact-groups/contacts/ids", { params: { q } });
    const before = new Set(selected);
    const added = d.ids.filter((id) => !before.has(id)).length;
    setSelected((s) => [...new Set([...s, ...d.ids])]);
    toast.success(added === 1 ? "1 contacto agregado" : `${added} contactos agregados`);
  };
  return (
    <div className="space-y-2" data-testid="contact-picker">
      <div className="flex gap-2">
        <Input placeholder="Buscar por nombre, teléfono o correo" value={q} onChange={(e) => setQ(e.target.value)} data-testid="contact-picker-search" />
        <Button variant="outline" size="sm" onClick={selectAll} data-testid="contact-picker-select-all">Seleccionar todos ({data.total})</Button>
        <Button variant="ghost" size="sm" onClick={() => setSelected([])} data-testid="contact-picker-clear">Limpiar</Button>
      </div>
      <div className="text-xs text-muted-foreground" data-testid="contact-picker-count">{selected.length} seleccionados · solo contactos con WhatsApp</div>
      <div className="border border-border rounded-md max-h-64 overflow-y-auto divide-y divide-border">
        {data.rows.map((c) => (
          <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-accent/40" data-testid={`contact-picker-row-${c.id}`}>
            <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} />
            <span className="flex-1 truncate">{c.name || "Sin nombre"}</span>
            <span className="font-mono text-muted-foreground">{c.phone}</span>
          </label>
        ))}
        {data.total > data.rows.length && <div className="px-3 py-1.5 text-[11px] text-muted-foreground">Mostrando {data.rows.length} de {data.total}. Busca o usa «Seleccionar todos».</div>}
      </div>
    </div>
  );
}

function GroupEditor({ group, onClose, onSaved }) {
  const [name, setName] = useState(group?.name || "");
  const [selected, setSelected] = useState(group?.customer_ids || []);
  const save = async () => {
    try {
      if (group?.id) await api.put(`/contact-groups/${group.id}`, { name, customer_ids: selected });
      else await api.post("/contact-groups", { name, customer_ids: selected });
      toast.success("Grupo guardado"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
  };
  return (
    <div className="space-y-3" data-testid="group-editor">
      <Input placeholder="Nombre del grupo, ej: Grupo de prueba" value={name} onChange={(e) => setName(e.target.value)} data-testid="group-name" />
      <ContactPicker selected={selected} setSelected={setSelected} />
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Volver</Button>
        <Button onClick={save} disabled={!name.trim() || !selected.length} data-testid="group-save">Guardar grupo ({selected.length})</Button>
      </DialogFooter>
    </div>
  );
}

export const ContactGroupsDialog = ({ onChange }) => {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/contact-groups").then(({ data }) => setGroups(data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  const remove = async (g) => {
    await api.delete(`/contact-groups/${g.id}`); toast.success("Grupo eliminado"); load(); onChange?.();
  };
  const edit = async (g) => setEditing(g ? (await api.get(`/contact-groups/${g.id}`)).data : {});
  return (
    <>
      <Button variant="outline" className="gap-1.5" onClick={() => setOpen(true)} data-testid="btn-contact-groups"><UsersThree size={14} /> Grupos de contactos</Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); setEditing(null); }}>
        <DialogContent className="max-w-2xl" data-testid="contact-groups-dialog">
          <DialogHeader>
            <DialogTitle>Grupos de contactos</DialogTitle>
            <DialogDescription>Crea grupos para enviar campañas de prueba o a un segmento específico.</DialogDescription>
          </DialogHeader>
          {editing ? (
            <GroupEditor group={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChange?.(); }} />
          ) : (
            <div className="space-y-2">
              <Button size="sm" className="gap-1" onClick={() => setEditing({})} data-testid="group-new"><Plus size={12} /> Nuevo grupo</Button>
              {groups.length === 0 && <div className="text-xs text-muted-foreground py-4">Aún no hay grupos.</div>}
              {groups.map((g) => (
                <div key={g.id} className="flex items-center gap-2 border border-border rounded-md px-3 py-2 text-sm" data-testid={`group-row-${g.id}`}>
                  <span className="flex-1">{g.name}</span>
                  <span className="text-xs text-muted-foreground">{g.count} contactos</span>
                  <Button size="icon" variant="ghost" onClick={() => edit(g)} data-testid={`group-edit-${g.id}`}><PencilSimple size={14} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(g)} data-testid={`group-delete-${g.id}`}><Trash size={14} /></Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContactGroupsDialog;
